import React, { useEffect, useState } from 'react';
import {
  SplashScreen,
  Spinner,
  useTranslation,
} from '@piwikpro/ui-components';
import { ConfigService, withInject } from '@piwikpro/platform';
import { Organization } from '@piwikpro/organization-crate/Organization';
import { useParams } from '@tanstack/react-router';

interface InjectedProps {
  organization: Organization
  Title: typeof React.Component
  config: ConfigService
}

interface Props extends InjectedProps {}

type Status =
  | 'invalid'
  | 'available'
  | 'not available'
  | 'loading'
  | 'error'

export const NoLicense = ({
  organization,
  Title,
  config,
}: Props) => {
  const { t } = useTranslation('administration');
  const params = useParams({ strict: false });
  const [status, setStatus] = useState<Status>('loading');

  const availableModules = config.get('AVAILABLE_MODULES') as Record<string, { id: string, label: string }>;
  const moduleId = params.moduleName === 'customer_data_platform'
    ? 'data_activation'
    : params.moduleName;
  const getModuleName = (id: string) => {
    const module = Object.values(availableModules).find(m => m.id === id);
    if (typeof module === 'undefined') {
      throw new Error(`Module "${id}" is invalid`);
    }

    return module.label;
  };

  useEffect(() => {
    if (!Object.values(availableModules).map(module => module.id).includes(moduleId ?? '')) {
      setStatus('invalid');

      return;
    }

    organization.fetchDetails()
      .then((response) => {
        const available = (response.license.current.modules as string[]).includes(moduleId ?? '');
        setStatus(available ? 'available' : 'not available');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [organization.fetchDetails, config.get, moduleId]);

  if (status === 'loading') {
    return <Spinner type="fullscreen" />;
  }

  if (status === 'error') {
    return (
      <Title part={t('components:splashScreen.internalServerError.title')} update>
        <SplashScreen type="internalServerError" />
      </Title>
    );
  }

  if (status === 'invalid') {
    return (
      <Title part={t('components:notFoundTitle')} update>
        <SplashScreen type="notFound" />
      </Title>
    );
  }

  if (status === 'not available') {
    return (
      <Title part={t('noLicense.title')}>
        <SplashScreen
          type="noLicenseForModule"
          interpolations={{ moduleName: getModuleName(moduleId ?? '') }}
        />
      </Title>
    );
  }

  if (status === 'available') {
    return (
      <SplashScreen
        type="activeLicenseForModule"
        interpolations={{ moduleName: getModuleName(moduleId ?? '') }}
      />
    );
  }

  throw new Error(`Unexpected status: "${status}"`);
};

export default withInject<InjectedProps>({
  organization: 'OrganizationCrate.organization',
  Title: 'TitleCrate.components.Title',
  config: 'config',
})(NoLicense);
